import { useMemo, useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";

function flatten(root) {
  const rows = [];
  const walk = (node, depth, id) => {
    const hasChildren = node.children && node.children.length > 0;
    rows.push({ id, node, depth, hasChildren });
    if (hasChildren) {
      node.children.forEach((c, i) => walk(c, depth + 1, `${id}.${i}`));
    }
  };
  walk(root, 0, "0");
  return rows;
}

export default function XmlTreeNode({ node }) {
  const rows = useMemo(() => flatten(node), [node]);
  const [collapsed, setCollapsed] = useState(() => {
    const s = new Set();
    rows.forEach((r) => {
      if (r.depth >= 2 && r.hasChildren) s.add(r.id);
    });
    return s;
  });

  const toggle = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isHidden = (id) => {
    for (const c of collapsed) {
      if (id !== c && id.startsWith(c + ".")) return true;
    }
    return false;
  };

  const visible = rows.filter((r) => !isHidden(r.id));

  return (
    <div data-testid="xml-tree" className="font-mono text-xs">
      {visible.map((r) => {
        const { node: n, depth, hasChildren, id } = r;
        const attrEntries = Object.entries(n.attributes || {});
        const open = !collapsed.has(id);
        return (
          <div
            key={id}
            onClick={() => hasChildren && toggle(id)}
            style={{ paddingLeft: depth * 16 }}
            className={`flex items-start gap-1 px-2 py-0.5 rounded-sm hover:bg-zinc-800/40 transition-colors duration-75 ${
              hasChildren ? "cursor-pointer" : ""
            }`}
          >
            <span className="w-4 shrink-0 mt-0.5 text-zinc-600">
              {hasChildren ? (
                open ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )
              ) : null}
            </span>
            <span className="leading-relaxed">
              <span className="text-blue-400">&lt;{n.tag}</span>
              {attrEntries.map(([k, v]) => (
                <span key={k}>
                  {" "}
                  <span className="text-amber-400">{k}</span>
                  <span className="text-zinc-600">=</span>
                  <span className="text-emerald-400">"{v}"</span>
                </span>
              ))}
              <span className="text-blue-400">&gt;</span>
              {!hasChildren && n.text ? (
                <>
                  <span className="text-zinc-200"> {n.text} </span>
                  <span className="text-blue-400">&lt;/{n.tag}&gt;</span>
                </>
              ) : null}
              {hasChildren && !open ? (
                <span className="text-zinc-600 italic"> … {n.children.length} </span>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}
